import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, CheckCircle2, Clock, AlertTriangle, MessageCircle,
  LogIn, Pencil, LogOut, Loader2, Trash2, Globe, Link2,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import { waLink, waMessages } from "@/lib/whatsapp";
import { PromoSettings } from "@/components/PromoSettings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const HEADING = "font-['Manrope',sans-serif]";
const STATUS_LABEL = { aktif: "Aktif", menunggu: "Menunggu Pembayaran", nonaktif: "Nonaktif", trial: "Trial" };
const STATUS_STYLE = {
  aktif: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  menunggu: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  nonaktif: "bg-red-500/15 text-red-400 border-red-500/30",
  trial: "bg-sky-500/15 text-sky-400 border-sky-500/30",
};
const INPUT_DARK = "border-white/15 bg-white/5 text-white placeholder:text-slate-500 focus-visible:ring-[#00D1A8]/50";

function offsetDate(days) {
  return new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
}

export default function SuperAdmin() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState(null);
  const [cfg, setCfg] = useState({ plans: [], product_name: "Aplikasi Sekolah Musik" });
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(() => {
    api
      .get("/admin/tenants")
      .then((r) => setTenants(r.data))
      .catch((e) => toast.error(apiError(e, "Gagal memuat data sekolah")));
  }, []);

  useEffect(() => {
    load();
    api.get("/public/config").then((r) => setCfg(r.data)).catch(() => {});
  }, [load]);

  const stats = useMemo(() => {
    const list = tenants || [];
    return {
      total: list.length,
      aktif: list.filter((t) => t.is_active).length,
      menunggu: list.filter((t) => t.status === "menunggu").length,
      overdue: list.filter((t) => t.status === "aktif" && t.due_date && t.due_date < today).length,
    };
  }, [tenants, today]);

  const openEdit = (t, activate = false) =>
    setEdit({
      id: t.id,
      name: t.name,
      plan: t.plan || "Basic",
      status: activate ? "aktif" : t.status,
      due_date: activate ? offsetDate(30) : t.due_date || "",
      notes: t.notes || "",
      domain: t.domain || "",
    });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/tenants/${edit.id}`, {
        plan: edit.plan,
        status: edit.status,
        due_date: edit.due_date || null,
        notes: edit.notes,
        domain: edit.domain,
      });
      toast.success(`Data ${edit.name} diperbarui`);
      setEdit(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Gagal menyimpan perubahan"));
    } finally {
      setSaving(false);
    }
  };

  const impersonate = async (t) => {
    try {
      await api.post(`/admin/tenants/${t.id}/impersonate`);
      await refresh();
      navigate("/dashboard");
    } catch (e) {
      toast.error(apiError(e, "Gagal masuk sebagai sekolah"));
    }
  };

  const waReminder = (t) => {
    const link = waLink(
      t.admin_phone,
      waMessages.subscription({
        adminName: t.admin_name,
        schoolName: t.name,
        plan: t.plan,
        dueDate: t.due_date ? formatDate(t.due_date) : null,
        productName: cfg.product_name,
      })
    );
    if (link) window.open(link, "_blank");
    else toast.error("Nomor WhatsApp admin sekolah belum tersedia");
  };

  const copyLoginLink = (t) => {
    const url = `${window.location.origin}/s/${t.slug}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success(`Link login ${t.name} disalin: ${url}`),
      () => toast.info(url)
    );
  };

  const deleteTenant = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/tenants/${del.id}`);
      toast.success(`Sekolah ${del.name} beserta seluruh datanya telah dihapus`);
      setDel(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Gagal menghapus sekolah"));
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#040D1D] font-['Plus_Jakarta_Sans',sans-serif] text-white" data-testid="superadmin-page">
      <header className="border-b border-white/10 bg-[#071731]/85 shadow-lg backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3.5">
          <div className="flex items-center gap-3">
            <img
              src="/nexora-logo.jpg"
              alt="Nexora"
              className="h-10 w-10 rounded-xl object-cover shadow-[0_0_18px_rgba(0,209,168,0.25)]"
            />
            <div className="leading-tight">
              <p className={`${HEADING} text-base font-extrabold tracking-tight text-white`}>Aplikasi Sekolah Musik</p>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#00D1A8]">Konsol Super Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-slate-400 sm:block" data-testid="superadmin-user-email">{user?.email}</span>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 rounded-xl border-white/20 bg-white/10 text-white hover:bg-white/20 hover:text-white"
              onClick={handleLogout}
              data-testid="superadmin-logout-button"
            >
              <LogOut className="h-4 w-4" /> Keluar
            </Button>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-6xl px-6 py-10">
        <div className="pointer-events-none absolute -top-10 right-10 h-72 w-72 rounded-full bg-[#2563EB]/10 blur-3xl" />
        <h1 className={`${HEADING} relative text-2xl font-extrabold tracking-tight text-white sm:text-3xl`}>
          Kelola Sekolah (Tenant)
        </h1>
        <p className="relative mt-1 text-sm text-slate-400">
          Aktivasi langganan, perpanjangan, dan pemantauan seluruh sekolah musik yang terdaftar.
        </p>

        <div className="relative mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: "Total Sekolah", value: stats.total, icon: Building2, testid: "stat-total-tenants", accent: "text-blue-400" },
            { label: "Langganan Aktif", value: stats.aktif, icon: CheckCircle2, testid: "stat-active-tenants", accent: "text-[#00D1A8]" },
            { label: "Menunggu Pembayaran", value: stats.menunggu, icon: Clock, testid: "stat-pending-tenants", accent: "text-amber-400" },
            { label: "Lewat Jatuh Tempo", value: stats.overdue, icon: AlertTriangle, testid: "stat-overdue-tenants", accent: "text-red-400" },
          ].map((s) => (
            <div
              key={s.label}
              className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-md transition-colors hover:border-white/20"
              data-testid={s.testid}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-400">{s.label}</p>
                <s.icon className={`h-4 w-4 ${s.accent}`} />
              </div>
              <p className={`${HEADING} mt-2 text-3xl font-extrabold text-white`}>{tenants === null ? "—" : s.value}</p>
            </div>
          ))}
        </div>

        <PromoSettings />

        <div className="relative mt-6 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md" data-testid="tenant-table">
          {tenants === null ? (
            <div className="flex justify-center p-10">
              <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
            </div>
          ) : tenants.length === 0 ? (
            <p className="p-10 text-center text-sm text-slate-500">Belum ada sekolah terdaftar.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-slate-400">Sekolah</TableHead>
                  <TableHead className="text-slate-400">Admin</TableHead>
                  <TableHead className="text-slate-400">Paket</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Jatuh Tempo</TableHead>
                  <TableHead className="text-right text-slate-400">Siswa</TableHead>
                  <TableHead className="text-right text-slate-400">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((t) => {
                  const overdue = t.status === "aktif" && t.due_date && t.due_date < today;
                  return (
                    <TableRow key={t.id} className="border-white/10 hover:bg-white/[0.04]" data-testid={`tenant-row-${t.id}`}>
                      <TableCell>
                        <p className="font-semibold text-white">{t.name}</p>
                        <p className="text-xs text-slate-500">Terdaftar {formatDate((t.created_at || "").slice(0, 10))}</p>
                        {t.slug && (
                          <button
                            type="button"
                            onClick={() => copyLoginLink(t)}
                            className="mt-0.5 flex items-center gap-1 text-xs text-slate-400 transition-colors hover:text-[#00D1A8]"
                            title="Salin link login khusus sekolah"
                            data-testid={`tenant-login-link-${t.id}`}
                          >
                            <Link2 className="h-3 w-3" /> /s/{t.slug}
                          </button>
                        )}
                        {t.domain && (
                          <p className="mt-0.5 flex items-center gap-1 text-xs text-[#00D1A8]" data-testid={`tenant-domain-${t.id}`}>
                            <Globe className="h-3 w-3" /> {t.domain}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-300">{t.admin_name}</p>
                        <p className="text-xs text-slate-500">{t.admin_email}</p>
                      </TableCell>
                      <TableCell className="text-sm text-slate-300">{t.plan || "-"}</TableCell>
                      <TableCell>
                        <span
                          className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLE[t.status] || "border-white/15 bg-white/10 text-slate-300"}`}
                          data-testid={`tenant-status-${t.id}`}
                        >
                          {STATUS_LABEL[t.status] || t.status}
                        </span>
                      </TableCell>
                      <TableCell className={`text-sm ${overdue ? "font-semibold text-red-400" : "text-slate-300"}`}>
                        {t.due_date ? formatDate(t.due_date) : "-"}
                        {overdue && <span className="block text-xs">Lewat jatuh tempo!</span>}
                      </TableCell>
                      <TableCell className="text-right text-sm text-slate-300">{t.student_count}</TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1.5">
                          {t.status === "menunggu" && (
                            <Button
                              size="sm"
                              className="rounded-lg bg-gradient-to-r from-[#2563EB] to-[#00D1A8] font-semibold text-white shadow-[0_0_15px_rgba(0,209,168,0.25)] hover:opacity-95"
                              onClick={() => openEdit(t, true)}
                              data-testid={`activate-tenant-btn-${t.id}`}
                            >
                              Aktifkan
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-white/15 bg-white/5 text-slate-300 hover:bg-white/15 hover:text-white"
                            onClick={() => openEdit(t)}
                            title="Kelola langganan"
                            data-testid={`edit-tenant-btn-${t.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#00D1A8]/30 bg-[#00D1A8]/10 text-[#00D1A8] hover:bg-[#00D1A8]/20 hover:text-[#00D1A8]"
                            onClick={() => waReminder(t)}
                            disabled={!t.admin_phone}
                            title="Ingatkan via WhatsApp"
                            data-testid={`wa-reminder-btn-${t.id}`}
                          >
                            <MessageCircle className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-white/15 bg-white/5 text-slate-300 hover:bg-white/15 hover:text-white"
                            onClick={() => impersonate(t)}
                            title="Masuk sebagai sekolah ini"
                            data-testid={`impersonate-btn-${t.id}`}
                          >
                            <LogIn className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 hover:text-red-300"
                            onClick={() => setDel(t)}
                            title="Hapus sekolah"
                            data-testid={`delete-tenant-btn-${t.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="border-white/10 bg-[#071731] text-white" data-testid="tenant-edit-dialog">
          <DialogHeader>
            <DialogTitle className={HEADING}>Kelola Langganan — {edit?.name}</DialogTitle>
          </DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Paket</Label>
                <Select value={edit.plan} onValueChange={(v) => setEdit({ ...edit, plan: v })}>
                  <SelectTrigger className={INPUT_DARK} data-testid="edit-plan-select">
                    <SelectValue placeholder="Pilih paket" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#071731] text-white">
                    {(cfg.plans.length ? cfg.plans : [{ name: "Basic" }, { name: "Pro" }, { name: "Premium" }]).map((p) => (
                      <SelectItem key={p.name} value={p.name} className="focus:bg-white/10 focus:text-white">{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Status</Label>
                <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                  <SelectTrigger className={INPUT_DARK} data-testid="edit-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#071731] text-white">
                    <SelectItem value="aktif" className="focus:bg-white/10 focus:text-white">Aktif</SelectItem>
                    <SelectItem value="menunggu" className="focus:bg-white/10 focus:text-white">Menunggu Pembayaran</SelectItem>
                    <SelectItem value="nonaktif" className="focus:bg-white/10 focus:text-white">Nonaktif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Jatuh Tempo Langganan</Label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={edit.due_date}
                    onChange={(e) => setEdit({ ...edit, due_date: e.target.value })}
                    className={`${INPUT_DARK} [color-scheme:dark]`}
                    data-testid="edit-due-date"
                  />
                  <Button type="button" variant="outline" size="sm" className="shrink-0 border-white/15 bg-white/5 text-slate-300 hover:bg-white/15 hover:text-white" onClick={() => setEdit({ ...edit, due_date: offsetDate(30) })} data-testid="quick-month-btn">
                    +1 Bulan
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="shrink-0 border-white/15 bg-white/5 text-slate-300 hover:bg-white/15 hover:text-white" onClick={() => setEdit({ ...edit, due_date: offsetDate(365) })} data-testid="quick-year-btn">
                    +1 Tahun
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Domain Khusus Sekolah (opsional)</Label>
                <Input
                  value={edit.domain}
                  onChange={(e) => setEdit({ ...edit, domain: e.target.value })}
                  placeholder="cth: gihoncantata.id"
                  className={INPUT_DARK}
                  data-testid="edit-domain"
                />
                <p className="text-xs text-slate-500">
                  Arahkan CNAME domain ini ke aplikasi & daftarkan di panel Publikasikan → Custom Domain. Pengunjung domain ini langsung melihat halaman login sekolah.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Catatan (internal)</Label>
                <Textarea
                  rows={2}
                  value={edit.notes}
                  onChange={(e) => setEdit({ ...edit, notes: e.target.value })}
                  placeholder="cth: Sudah transfer 5 Juni, cek mutasi"
                  className={INPUT_DARK}
                  data-testid="edit-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} className="border-white/20 bg-white/10 text-white hover:bg-white/20 hover:text-white" data-testid="cancel-tenant-edit">
              Batal
            </Button>
            <Button onClick={save} disabled={saving} className="bg-gradient-to-r from-[#2563EB] to-[#00D1A8] font-semibold text-white shadow-[0_0_20px_rgba(0,209,168,0.3)] hover:opacity-95" data-testid="save-tenant-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="border-white/10 bg-[#071731] text-white" data-testid="delete-tenant-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className={HEADING}>Hapus Sekolah &quot;{del?.name}&quot;?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Tindakan ini <strong className="text-red-400">tidak dapat dibatalkan</strong>. Seluruh data sekolah —
              siswa, guru, kelas, jadwal, pembayaran, dan laporan keuangan — akan dihapus permanen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/20 bg-white/10 text-white hover:bg-white/20 hover:text-white" data-testid="cancel-delete-tenant">
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteTenant}
              disabled={deleting}
              className="bg-red-600 font-semibold text-white hover:bg-red-500"
              data-testid="confirm-delete-tenant"
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Hapus Permanen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
